"use client";

import React, { useMemo, useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "../../../../components/navbar";
import Footer from "../../../../components/footer";
import Table from "../../../../components/Table.jsx";
import Button from "../../../../components/Button.jsx";
import Pagination from "../../../../components/Pagination.jsx";
import styles from "../css/ConsultationHistory.module.css";

export default function ConsultationHistoryDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [serviceFilter, setServiceFilter] = useState("All");
  const [page, setPage] = useState(1);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (id) navigate(`/customer/clinic/consultation-history/${id}/services-done`, { replace: true });
  }, [id, navigate]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const { getPatientServiceSummary } = await import("../../../../api/clinic/consultationApi.js");
        const data = await getPatientServiceSummary(id);
        if (!mounted) return;
        setServices(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!mounted) return;
        setError(e.message || 'Failed to load services');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false };
  }, [id]);

  const filtered = useMemo(() => {
    if (serviceFilter === "All") return services;
    return services.filter((s) => (s.servicename || "") === serviceFilter);
  }, [serviceFilter, services]);

  const pageSize = 3;
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const start = (page - 1) * pageSize;
  const current = filtered.slice(start, start + pageSize);

  const columns = [
    { header: <div className={styles.colMin280}>Type Of Service</div>, accessor: "servicename" },
    { header: <div className={styles.colMin180}>Number of visit</div>, accessor: "visits" },
    {
      header: <div className={styles.colMin140}>Action</div>,
      accessor: "serviceid",
      render: (_val, row) => (
        <Button variant="primary" onClick={() => navigate(`/customer/clinic/consultation-history/${id}/service/${row.serviceid}`)}>
          View
        </Button>
      ),
    },
  ];

  return (
    <div className={styles.pageWrapper}>
      <Navbar />
      <main className={styles.container}>
        <h2 className={styles.pageTitle}>Consultation History</h2>

        <section className={styles.detailsCard}>
          <div className={styles.controlsRow}>
            <label className={styles.selectLabel}>Type Of Service</label>
            <select
              className={styles.selectControl}
              value={serviceFilter}
              onChange={(e) => { setServiceFilter(e.target.value); setPage(1); }}
            >
              <option value="All">All</option>
              {services.map((s) => (
                <option key={s.serviceid} value={s.servicename}>{s.servicename}</option>
              ))}
            </select>
          </div>

          <div className={styles.innerTableWrap}>
            {error && (
              <div className={styles.inlineError}>{error}</div>
            )}
            {loading ? (
              <div className={styles.loadingMessage}>Loading...</div>
            ) : (
              <Table columns={columns} data={current} />
            )}
            <div className={styles.paginationRow}>
              <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
